/**
 * Button to flag a comment as inappropriate. Visible to authenticated users (not author).
 * Comment is hidden from public list until a moderator judges.
 */

import React, { useState } from 'react';
import { commentsApi } from '../../api/comments';
import { useAuth } from '../../auth/useAuth';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { Textarea } from '../common/Textarea';
import { useLanguage } from '../../i18n/useLanguage';

interface FlagCommentButtonProps {
  commentId: string;
  authorId: string;
  onFlagged?: () => void;
  variant?: 'text' | 'button';
  className?: string;
}

export const FlagCommentButton: React.FC<FlagCommentButtonProps> = ({
  commentId,
  authorId,
  onFlagged,
  variant = 'button',
  className,
}) => {
  const { t } = useLanguage();
  const { user, isAuthenticated } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAuthor = isAuthenticated && user?.sub === authorId;
  if (!isAuthenticated || isAuthor) {
    return null;
  }

  const handleOpen = () => {
    setShowModal(true);
    setReason('');
    setError(null);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      await commentsApi.flagComment(commentId, reason.trim() || undefined);
      setShowModal(false);
      onFlagged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('moderation.failedFlagComment'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {variant === 'text' ? (
        <button
          type="button"
          className={`flag-comment-link ${className ?? ''}`}
          onClick={handleOpen}
          aria-label={t('moderation.flagCommentAria')}
        >
          {t('moderation.report')}
        </button>
      ) : (
        <Button variant="outline" size="small" onClick={handleOpen} className={className}>
          {t('moderation.report')}
        </Button>
      )}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setReason('');
          setError(null);
        }}
        title={t('moderation.flagInappropriateTitle')}
      >
        <div className="moderation-modal">
          <p>{t('moderation.flagCommentExplain')}</p>
          <Textarea
            label={t('moderation.reasonOptional')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('moderation.flagReasonOptionalPlaceholder')}
            rows={3}
          />
          {error && <div className="error-message">{error}</div>}
          <div className="modal-actions">
            <Button variant="danger" onClick={handleSubmit} disabled={loading}>
              {loading ? t('moderation.submitting') : t('moderation.flagCommentSubmit')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                setReason('');
                setError(null);
              }}
              disabled={loading}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
